import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Fade,
  Grid,
  Typography,
} from '@mui/material';
import { getWindow, trackEvent } from '@surfline/web-common';

import CloudflareResizedImage from 'components/CloudflareResizedImage';
import { PlayIcon } from 'components/Icons';
import { fetchEditorialTaxonomyPosts } from 'common/api/editorial';
import { AppState } from 'stores';
import type { RecentContent } from 'types/editorial';
import getFeedGeotarget from 'utils/getFeedGeotarget';

import Styles from './TravelRecentArticles.module.scss';

interface Category {
  url: string;
  name: string;
  slug: string;
  imageUrl: string;
  taxonomy: string;
}

interface Props {
  ssrReduxState: AppState;
}

const TravelRecentArticles: React.FC<Props> = (props) => {
  const { ssrReduxState } = props;
  let geotarget = 'US';
  if (ssrReduxState) geotarget = getFeedGeotarget(ssrReduxState) || 'US';
  const limit = '12';
  const initialOffset = '0';
  const [recentTravelPosts, setRecentTravelPosts] = useState<Array<RecentContent>>([]);
  const [disableLoadMore, setDisableLoadMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const { asPath } = useRouter();

  const fetchRecentPosts = async (offset: string) => {
    setLoading(true);
    try {
      const JSONData = await fetchEditorialTaxonomyPosts({
        taxonomy: 'category',
        term: 'travel',
        geotarget,
        limit,
        offset,
        isServer: false,
      });
      const posts = JSONData?.posts ?? [];
      if (offset !== initialOffset && posts.length === 0) {
        setDisableLoadMore(true);
      }
      if (recentTravelPosts.length === 0) {
        setRecentTravelPosts([...posts]);
      } else {
        setRecentTravelPosts((prev) => [...prev, ...posts]);
      }
    } catch {
      setDisableLoadMore(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMoreButton = async (offset: string) => {
    fetchRecentPosts(offset);
  };

  const trackCardClick = (post: RecentContent, index: number) => {
    trackEvent('Carousel Clicked', {
      carouselItem: post?.title,
      carouselName: 'landing page playlist all content',
      category: 'travel',
      path: asPath,
      positionNumber: index,
      postId: post?.id,
      postCategories: post?.categories
        ?.map((category: { name: string }) => category.name)
        .join(','),
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow().location.search,
      title: 'travel homepage',
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
    });
  };

  const isVideo = (category: Category) => category.slug === 'video';

  useEffect(() => {
    fetchRecentPosts(initialOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      className={classNames(Styles.recentPosts, 'sl-section-container')}
      data-testid="travel-recent"
    >
      <Typography variant="title2" color="text.primary" className={Styles.title}>
        Latest Travel Stories
      </Typography>
      <Grid container spacing={{ mobile: 2, tablet: 2, tabletLg: 3, desktop: 3 }}>
        {recentTravelPosts.length
          ? recentTravelPosts.map((post: RecentContent, index) => (
              <Grid item mobile={12} tablet={6} tabletLg={4} desktop={3} key={post.id}>
                <Card className={Styles.gridCard} data-testid="travel-recent-articles-item">
                  <a
                    target="_blank"
                    href={post.permalink}
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackCardClick(post, index);
                    }}
                  >
                    <CardActionArea
                      classes={{
                        focusHighlight: Styles.focusHighlight,
                      }}
                      className={Styles.actionArea}
                      disableRipple
                      disableTouchRipple
                    >
                      <div className={Styles.cardMediaWrap}>
                        {post.media?.feed1x && (
                          <CardMedia
                            className={Styles.cardImage}
                            data-testid="travel-recent-articles-item-image"
                          >
                            <CloudflareResizedImage
                              src={post.media.feed1x}
                              alt=""
                              layout="fill"
                              objectFit="cover"
                              objectPosition="center"
                              loading="lazy"
                              sizes="(max-width: 599px) calc(100vw - 80px), (max-width: 899px) calc(50vw - 80px), (max-width: 1199px) calc(33.333vw - 80px), (min-width: 1200px) calc(25vw - 80px)"
                            />
                          </CardMedia>
                        )}
                        {post?.categories.some(isVideo) ? (
                          <PlayIcon className={Styles.videoIcon} />
                        ) : null}
                      </div>
                      <CardContent className={Styles.cardContent}>
                        <Typography
                          variant="headline"
                          component="h3"
                          color="text.primary"
                          className={Styles.cardTitle}
                        >
                          {post.title}
                        </Typography>
                        <Typography
                          sx={{
                            marginBottom: '16px',
                          }}
                        >
                          {post.subtitle}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </a>
                </Card>
              </Grid>
            ))
          : null}
      </Grid>
      {!disableLoadMore ? (
        <div className={Styles.loadMore}>
          <Box sx={{ height: 40 }}>
            <Fade
              in={loading}
              style={{
                transitionDelay: loading ? '1000ms' : '0ms',
              }}
              unmountOnExit
            >
              <CircularProgress disableShrink />
            </Fade>
          </Box>
          {!loading && (
            <Button
              disableRipple
              disableTouchRipple
              onClick={() => handleLoadMoreButton(recentTravelPosts.length.toString())}
              size="small"
              variant="secondary"
            >
              Load More
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
};
export default TravelRecentArticles;
